import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Container,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    Paper
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { storage } from '../../../config/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import Resizer from 'react-image-file-resizer';
import { UserAvatar } from './userAvatar';

// Move styled component outside to prevent recreation on each render
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const ProfileDetails = ({ userData: initialUserData, saveProfile, uid, email }) => {
    const navigate = useNavigate();
    const [userData, setUserData] = useState({
        name: initialUserData?.name || '',
        bio: initialUserData?.bio || '',
        picurl: initialUserData?.picurl || ''
    });
    const [nameError, setNameError] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const inputNameRef = useRef(null);

    const handleSave = useCallback((data) => {
        saveProfile(data);
    }, [saveProfile]);

    const submitForm = useCallback((event) => {
        event.preventDefault();

        const form = event.target;
        const name = form.name.value;
        const bio = form.bio.value;
        // Add more fields if you expand the form in the future

        if (!form.name.validity.valid) {
            setNameError(true);
            form.name.focus();
            return;
        }

        const updatedUserData = {
            ...userData,
            name,
            bio,
        };

        setUserData(updatedUserData);
        handleSave(updatedUserData);
    }, [userData, handleSave]);

    const handleChange = useCallback((e) => {
        const { name, value, validity } = e.target;
        setNameError(!validity.valid);
        setUserData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const uploadImage = useCallback(async (uri) => {
        if (!uri) return;
        
        try {
            setIsUploading(true);
            setUploadError(null);
            
            const blob = await fetch(uri).then((r) => r.blob());
            const fileFromBlob = new File([blob], 'avatar.png', { type: 'image/jpeg' });
            const imageRef = ref(storage, `avatars/${uid}.jpeg`);
            
            await uploadBytes(imageRef, fileFromBlob);
            const url = await getDownloadURL(imageRef);
            
            setUserData((prev) => {
                const newUserData = { ...prev, picurl: url };
                handleSave(newUserData); // pass the updated data with picurl
                return newUserData;
            });
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
        }
    }, [uid, handleSave]);

    const resizeAndUploadPic = useCallback((file) => {
        if (!file) return;
        
        Resizer.imageFileResizer(
            file,
            128,
            128,
            'JPEG',
            80,
            0,
            (resizedPicUri) => {
                uploadImage(resizedPicUri);
            },
            'base64',
            64,
            64
        );
    }, [uploadImage]);

    const handleMyProjects = useCallback(() => {
        navigate('/myprojects');
    }, [navigate]);

    const handleFileChange = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file) {
            resizeAndUploadPic(file);
        }
    }, [resizeAndUploadPic]);

    // Memoize computed values
    const nameHelperText = useMemo(() => 
        nameError ? 'Please enter your name' : `${userData.name.length}/100 characters`,
        [nameError, userData.name.length]
    );

    const bioHelperText = useMemo(() => 
        `${userData.bio.length}/200 characters`,
        [userData.bio.length]
    );

    return (
    <Container
        maxWidth="sm"
        sx={{ pt: { xs: 4, sm: 6 }, pb: { xs: 6, sm: 8 } }}
    >
        <Stack spacing={4}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    p: { xs: 2.5, sm: 3 }
                }}
            >
                <Grid container rowSpacing={3} columnSpacing={3} alignItems="center">
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                        <UserAvatar userData={userData} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Stack spacing={1} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="h4">
                                {userData.name || 'Your Name'}
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 3,
                                }}
                            >
                                {userData.bio || 'Share a short bio to introduce yourself.'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={1.5} alignItems="center">
                            <Button
                                component="label"
                                size="small"
                                variant="contained"
                                disabled={isUploading}
                                startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                                onChange={handleFileChange}
                            >
                                {isUploading ? 'Uploading...' : 'Upload avatar'}
                                <VisuallyHiddenInput 
                                    type="file" 
                                    accept="image/jpeg, image/png" 
                                    disabled={isUploading}
                                />
                            </Button>
                            {uploadError && (
                                <Alert severity="error" sx={{ width: '100%' }}>
                                    {uploadError}
                                </Alert>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    p: { xs: 2.5, sm: 3 }
                }}
                component="form"
                autoComplete="off"
                onSubmit={submitForm}
                noValidate
            >
                <Stack spacing={2.5}>
                    <Box>
                        <Typography variant="h6" sx={{ mb: 0.5 }}>
                            Profile details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Update your basic information so teammates recognize you.
                        </Typography>
                    </Box>
                    <TextField
                        id="name"
                        name="name"
                        variant="outlined"
                        label="Name"
                        inputRef={inputNameRef}
                        fullWidth
                        required
                        inputProps={{ maxLength: 100 }}
                        value={userData.name}
                        onChange={handleChange}
                        error={nameError}
                        helperText={nameHelperText}
                    />
                    <TextField
                        id="bio"
                        name="bio"
                        variant="outlined"
                        label="Bio"
                        multiline
                        rows={4}
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                        value={userData.bio}
                        onChange={handleChange}
                        helperText={bioHelperText}
                    />
                    <TextField
                        variant="outlined"
                        disabled
                        size="small"
                        label="Email"
                        fullWidth
                        value={email || ''}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                        <Button variant="outlined" color="inherit" onClick={handleMyProjects}>
                            View my projects
                        </Button>
                        <Button variant="contained" type="submit">
                            Save changes
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Stack>
    </Container>
    );
};